import { useQuery } from '@tanstack/react-query';
import { fromNullable, nothing } from 'maybeasy';
import { useEffect, useRef } from 'react';
import { Task } from 'taskarian';
import { decodeOnSelect, fetchJson } from '../../../Appy';
import { currentUserStore } from '../../../CurrentUserStore';
import { findLinkT } from '../../../LinkyLinky';
import { Link } from '../../../Resource/Types';
import { StringOption } from '../../Options/Types';
import { learningCollectionSummariesResourceDecoder } from '../Decoders';
import LearningCollectionsStore from '../Store';
import {
  LearningCollectionSummariesResource,
  LearningCollectionsError,
  LearningCollectionsState,
} from '../Types';
import { assignSearchParameters } from './SearchSortPaginate';

const fetchData = (state: LearningCollectionsState) =>
  Task.succeed<LearningCollectionsError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('learning-collections-index'))
    .andThen(assignSearchParameters(state.page, state.searchParameters, state.sortParameters))
    .andThen(fetchJson)
    .resolve();

export const useLearningCollections = (learningCollectionsStore: LearningCollectionsStore) => {
  const { state } = learningCollectionsStore;

  // Only use the actual query parameters, not the loading state
  const page = state.kind === 'ready' ? state.page : 1;
  const searchParameters = state.kind === 'ready' ? state.searchParameters : [];
  const sortParameters = state.kind === 'ready' ? state.sortParameters : [];
  const selectedOrganization =
    state.kind === 'ready' ? state.selectedOrganization : nothing<StringOption>();

  const organizationId = selectedOrganization.map((o) => o.value).getOrElseValue('none');
  const searchParamsKey = JSON.stringify(searchParameters);
  const sortParamsKey = JSON.stringify(sortParameters);

  // Track if we've updated the store for this data
  const lastDataKeyRef = useRef<string>('');

  const { data, error, isLoading, status, dataUpdatedAt } = useQuery<
    string,
    Error,
    LearningCollectionSummariesResource | undefined
  >({
    queryKey: ['learning-collections-index', page, organizationId, searchParamsKey, sortParamsKey],
    queryFn: () =>
      fetchData({
        ...state,
        page,
        searchParameters,
        sortParameters,
        selectedOrganization,
      } as any),
    select: decodeOnSelect(learningCollectionSummariesResourceDecoder),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (status === 'success' && data) {
      // Use React Query's dataUpdatedAt timestamp to detect when data actually changes
      // This detects both new collections (totalCount changes) AND field changes (like published_at)
      const dataKey = `${state.page}-${organizationId}-${searchParamsKey}-${sortParamsKey}-${dataUpdatedAt}`;

      // Only update if this is genuinely new data
      if (dataKey !== lastDataKeyRef.current) {
        lastDataKeyRef.current = dataKey;
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- TODO: Lifting store ownership here would require a major refactor of the LearningCollections data flow
        learningCollectionsStore.ready(fromNullable(data));
      }
    }
    // Note: learningCollectionsStore is intentionally not in deps to avoid infinite loops
    // The store reference is stable and we only care about data changes, not store mutations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, status, state.page, organizationId, searchParamsKey, sortParamsKey, dataUpdatedAt]);
  return {
    isLoading,
    error,
  };
};
